import { Divider } from '@blueprintjs/core'
import React from 'react'
import styled from 'styled-components'
import { formatBRL } from '../../cash-register/common/formatBRL'
import { mobile } from '../../common/assets/utils/_breakpoints'
import { DueDateRule, SourceProduct } from '../../services/bff/coordinator/dto/Contracts.dto'
import CustomButton from '../../ui/Button/CustomButton'
import { green, white } from '../../ui/utils/_colors'
import ListItem from './components/ListItem'
import PurchaseDetailsDropdown from './PurchaseDetailsDropdown'
import SmartphoneSalePurchaseDetails from './SmartphoneSalePurchaseDetails'
import { tablet } from '../../legacy-lib/design-kit-ui/utils/_breakpoints'
import InterestPaidByRetailerPurchaseDetails from './InterestPaidByRetailerPurchaseDetails'
import { SourceProductHelper } from '../../common/SourceProductHelper'

interface Props {
  purchaseValue: string
  interestRate: number
  lateInterestRate: number
  iof: number
  installments: number
  installmentValue: number
  installmentValueWithoutInterest?: number
  installmentValueWithoutBonification: number
  totalPurchaseValue: number
  couponDiscount: number
  firstInstallmentDueDate?: string
  selectedPurchaseCondition: number
  openVerificationDialog: () => void
  customerFeeRate?: number
  dueDateRule?: DueDateRule
  sourceProduct?: string
  downPayment?: number
  purchaseValueMinusDownPayment?: number
  customerFeeValue?: number
  principal: number
  productPrice?: number
}

export const PurchaseResumeCard: React.FC<Props> = (props) => {
  const {
    purchaseValue,
    interestRate,
    lateInterestRate,
    iof,
    installments,
    installmentValue,
    installmentValueWithoutBonification,
    totalPurchaseValue,
    couponDiscount,
    firstInstallmentDueDate,
    selectedPurchaseCondition,
    openVerificationDialog,
    installmentValueWithoutInterest,
    customerFeeRate,
    dueDateRule,
    sourceProduct,
    downPayment,
    purchaseValueMinusDownPayment,
    customerFeeValue,
    principal,
    productPrice,
  } = props

  const renderPurchaseContractValues = () => {
    if (SourceProductHelper.isInterestPaidByRetailer(sourceProduct)) {
      return (
        <ListItem
          label="Total a pagar"
          value={<PurchaseFinalValue>{formatBRL(totalPurchaseValue)}</PurchaseFinalValue>}
        />
      )
    }

    return (
      <>
        <ListItem
          label="Valor do contrato"
          value={formatBRL(totalPurchaseValue + couponDiscount)}
        />

        {couponDiscount > 0 && (
          <ListItem
            label="Cupom aplicado"
            value={<CuponValue>- {formatBRL(couponDiscount)}</CuponValue>}
          />
        )}

        <Divider />

        <ListItem
          label="Total a pagar"
          value={<PurchaseFinalValue>{formatBRL(totalPurchaseValue)}</PurchaseFinalValue>}
        />

        {renderFinalizePurchaseButton()}
      </>
    )
  }

  const renderFinalizePurchaseButton = () => (
    <FinalizePurchaseButton
      width="100%"
      height="56px"
      borderRadius="8px"
      disabled={selectedPurchaseCondition === -1}
      tabIndex={1}
      onClick={openVerificationDialog}
    >
      Finalizar compra
    </FinalizePurchaseButton>
  )

  const renderPurchaseDetails = () => {
    switch (sourceProduct) {
      case SourceProduct.SMARTPHONE_SALE:
        return (
          <SmartphoneSalePurchaseDetails
            interestRate={interestRate}
            iof={iof}
            firstInstallmentDueDate={firstInstallmentDueDate}
            couponDiscount={couponDiscount}
            installmentValue={installmentValue}
            customerFeeRate={customerFeeRate}
            downPayment={downPayment}
            purchaseValueMinusDownPayment={purchaseValueMinusDownPayment}
            customerFeeValue={customerFeeValue}
            principal={principal}
            productPrice={productPrice}
          />
        )
      case SourceProduct.INTEREST_PAID_BY_RETAILER:
        return (
          <>
            <InterestPaidByRetailerPurchaseDetails
              principal={principal}
              totalPurchaseValue={totalPurchaseValue}
              lateInterestRate={lateInterestRate}
              iof={iof}
              numberOfInstallments={installments}
              firstInstallmentDueDate={firstInstallmentDueDate}
              couponDiscount={couponDiscount}
              installmentValue={installmentValue}
              installmentValueWithoutInterest={installmentValueWithoutInterest}
              installmentValueWithoutBonification={installmentValueWithoutBonification}
              dueDateRule={dueDateRule}
            />
            {renderFinalizePurchaseButton()}
          </>
        )
      default:
        return (
          <PurchaseDetailsDropdown
            purchaseValue={purchaseValue}
            interestRate={interestRate}
            iof={iof}
            firstInstallmentDueDate={firstInstallmentDueDate}
            couponDiscount={couponDiscount}
            installmentValue={installmentValue}
            installmentValueWithoutInterest={installmentValueWithoutInterest}
            customerFeeRate={customerFeeRate}
            dueDateRule={dueDateRule}
          />
        )
    }
  }

  return (
    <PurchaseResumeContainer>
      <Title>Resumo do contrato</Title>
      <PurchaseInfoGroup>
        {renderPurchaseContractValues()}
        {renderPurchaseDetails()}
      </PurchaseInfoGroup>
    </PurchaseResumeContainer>
  )
}

export default PurchaseResumeCard

const PurchaseResumeContainer = styled.div`
  width: 90%;
  margin: 20px;
  border-radius: 20px;
  background-color: white;
  @media (max-width: ${tablet}) {
    margin-top: 0px;
  }
`

const PurchaseInfoGroup = styled.div`
  padding: 25px;

  @media (max-width: ${mobile}) {
    padding: 10px 0px 0px 0px;
  }
`

const Title = styled.div`
  width: 100%;
  padding-top: 32px;
  padding-left: 32px;
  font-size: 20px;
  font-weight: bold;
  font-family: Inter;
  text-align: start;
`

const CuponValue = styled.text`
  font-weight: bold;
  color: #46db6b; // light green
`

const PurchaseFinalValue = styled.text`
  font-size: 24px;
`

const FinalizePurchaseButton = styled(CustomButton)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-color: ${green.primary};
  background-color: ${green.primary};
  color: ${white.primary};
  margin: 12px 0px 0px 0px;
  font-size: 18px;
`
